import React, { useEffect, useState } from "react";
import { Download, FileText, Presentation, Target } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export const ClientProfilingPanel = ({ clientId }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${clientId}/profiling`)
    .then((r) => { setD(r.data); setErr(null); })
    .catch((e) => { setD(null); setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const setAccess = async (on) => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}`, { profiling_access: on });
      toast.success(on
        ? "Profiling freigeschaltet – der Teilnehmer sieht es ab sofort in seinem Portal"
        : "Zugang entzogen – das Profiling ist im Portal nicht mehr sichtbar");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const openPdf = async (kind) => {
    try {
      const r = await api.get(`/clients/${clientId}/profiling/pdf`, { responseType: "blob", timeout: 300000, params: { kind } });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (err) {
    return (
      <div className="border border-slate-200 p-5" data-testid="akte-profiling-error">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Profiling (Stellenanalyse & Profil)</div>
        <p className="text-sm text-slate-600 mt-2">{err}</p>
      </div>
    );
  }
  if (!d) return null;
  if (!d.access) {
    return (
      <div className="border border-slate-200 p-5" data-testid="akte-profiling-locked">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Profiling (Stellenanalyse & Profil)</div>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Noch nicht freigeschaltet. Mit einem Klick bekommt der Teilnehmer im Portal die interaktive
          Standortbestimmung: Hard & Soft Skills, Werte, Wunschaufgaben, Gehaltsvorstellung, Deal Maker/Breaker –
          und danach je Berufsbild (Plan A und Plan B) die Analyse von drei konkreten Stellen. Am Ende entstehen
          die Stellenanalyse mit Kompetenzabgleich und ein Ein-Seiten-Profil als PDF.
        </p>
        <button onClick={() => setAccess(true)} disabled={busy} data-testid="akte-profiling-enable"
          className="mt-4 px-4 py-2.5 text-xs font-medium bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-1.5">
          <Target size={12} strokeWidth={1.5} /> {busy ? "Wird freigeschaltet…" : "Profiling freischalten"}
        </button>
      </div>
    );
  }

  const ai = d.ai || {};
  const careers = d.careers || {};

  return (
    <div className="border border-slate-200 p-5" data-testid="akte-profiling-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Profiling (Stellenanalyse & Profil)</div>
          <div className="text-sm text-slate-700 mt-1">
            {d.answered} von {d.total} Schritten ausgefüllt · {d.jobs_filled || 0} Stellen analysiert
            {d.generated_at ? " · Abgleich erstellt" : " · Abgleich noch nicht erstellt"}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`/mein-profiling?client_id=${clientId}`} data-testid="akte-profiling-open"
            className="px-3 py-2 text-xs border border-slate-900 text-slate-900 hover:bg-slate-100 flex items-center gap-1.5">
            <Presentation size={12} strokeWidth={1.5} /> Präsentation öffnen
          </a>
          <button onClick={() => setAccess(false)} disabled={busy} data-testid="akte-profiling-disable"
            className="px-3 py-2 text-xs border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            title="Zugang entziehen – der Teilnehmer sieht das Profiling dann nicht mehr">
            Zugang entziehen
          </button>
        </div>
      </div>

      <div className="h-[3px] bg-slate-200 mt-4">
        <div className="h-full bg-slate-900" style={{ width: `${(d.answered / (d.total || 1)) * 100}%` }} />
      </div>

      {d.answered >= 4 && d.jobs_filled > 0 && (
        <div className="flex gap-2 flex-wrap mt-4">
          <button onClick={() => openPdf("summary")} data-testid="akte-profiling-pdf-summary"
            className="px-3 py-2 text-xs bg-slate-900 text-white hover:bg-black flex items-center gap-1.5">
            <FileText size={12} strokeWidth={1.5} /> Stellenanalyse & Abgleich (PDF)
          </button>
          <button onClick={() => openPdf("profile")} data-testid="akte-profiling-pdf-profile"
            className="px-3 py-2 text-xs border border-slate-900 hover:bg-slate-100 flex items-center gap-1.5">
            <Download size={12} strokeWidth={1.5} /> Ein-Seiten-Profil (PDF)
          </button>
        </div>
      )}

      {ai.headline && (
        <div className="mt-5 border-t border-slate-200 pt-4" data-testid="akte-profiling-ai">
          <div className="text-[11px] text-slate-500">Zielposition laut Abgleich</div>
          <div className="text-sm font-medium text-slate-900">{ai.headline}</div>
          {ai.recommendation && <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{ai.recommendation}</p>}
        </div>
      )}

      <details className="mt-5 border-t border-slate-200 pt-4" data-testid="akte-profiling-answers">
        <summary className="text-xs font-medium text-slate-700 cursor-pointer hover:text-slate-900">
          Antworten des Teilnehmers anzeigen
        </summary>
        <div className="mt-4 space-y-4">
          {(d.steps || []).filter((s) => s.type !== "career").map((s) => {
            const v = (d.answers || {})[s.key];
            const text = Array.isArray(v) ? v.join(", ") : v;
            return (
              <div key={s.key}>
                <div className="text-[11px] text-slate-500">{s.title}</div>
                <div className="text-sm text-slate-900 whitespace-pre-line">{text || "—"}</div>
              </div>
            );
          })}
          {(d.career_defs || []).map(({ key, label }) => {
            const cc = careers[key] || {};
            const jobs = (cc.jobs || []).filter((j) => (j?.position || j?.requirements || "").trim());
            if (!jobs.length && !cc.title) return null;
            return (
              <div key={key} className="border-t border-slate-100 pt-4">
                <div className="text-[11px] font-bold uppercase tracking-overline text-slate-500">{label}</div>
                <div className="text-sm font-medium text-slate-900">{cc.title || "—"}</div>
                <div className="grid gap-3 sm:grid-cols-3 mt-3">
                  {jobs.map((j, i) => (
                    <div key={i} className="border border-slate-200 p-3" data-testid={`akte-profiling-job-${key}-${i}`}>
                      <div className="text-[11px] text-slate-500">Stelle {i + 1}</div>
                      <div className="text-sm font-medium text-slate-900">{j.position || "—"}</div>
                      {j.link && <a href={j.link} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 underline break-all">Anzeige öffnen</a>}
                      {(d.job_fields || []).filter((f) => !["position", "link"].includes(f.key)).map((f) => (
                        j[f.key] ? (
                          <div key={f.key} className="mt-2">
                            <div className="text-[10px] uppercase tracking-overline text-slate-400">{f.label.replace(/^\d+\.\s*/, "")}</div>
                            <div className="text-xs text-slate-700 whitespace-pre-line">{j[f.key]}</div>
                          </div>
                        ) : null
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
};

export default ClientProfilingPanel;
